import type { SceneConfig } from './sceneConfig';
import { resolveCdnUrl } from '../utils/cdnUrl';

const OVERALL_2M_URL = resolveCdnUrl('models/overall/Overall_v8_2000K.sog');
const OVERALL_1M_URL = resolveCdnUrl('models/overall/Overall_v8_1000K.sog');
const OVERALL_600K_URL = resolveCdnUrl('models/overall/Overall_v8_600K.sog');

// Two distinct orbit anchors. See docs/architecture/scene-pipeline.md → "Split orbit center
// (preview-tour vs main scene)" for the phase-switch lifecycle.
// Invariant: `camera.previewTourEnd.target === MAIN_ORBIT_CENTER`.
const PREVIEW_ORBIT_CENTER = { x: 1.27, y: 0, z: -0.18 };
const MAIN_ORBIT_CENTER = { x: 0.18, y: 0.03, z: -0.31 };
const WAVES_CENTER = PREVIEW_ORBIT_CENTER;
const REVEAL_CENTER = { x: 2.46, y: 0, z: -0.36 };

export const overallSceneConfig: SceneConfig = {
  id: 'overall',
  renderer: {
    clearColor: 0x0f0f13,
  },
  camera: {
    fov: 60,
    near: 0.01,
    far: 1e4,
    initialPosition: { x: 2.01, y: 0.66, z: -0.3 },
    previewTourStart: {
      "x": 4.331687195745307,
      "y": 4.213244437154379,
      "z": -1.9476659266379823
    },
    previewTourEnd: {
      position: { x: 5.82, y: 2.08, z: -0.47 },
      // Must match `controls.orbitCenter` (= MAIN_ORBIT_CENTER) — invariant
      // for split-orbit-center phase switch. See docs/architecture/scene-pipeline.md.
      target: MAIN_ORBIT_CENTER,
    },
  },
  controls: {
    // Y-clamps disabled. `defaultCameraConfig` ships with
    // `clampMinHeight: 0.08, clampMaxHeight: 0.43, clampMaxHeightFar: 0.20`
    // — calibrated for kaaba/arafat where the camera lives near y=0.1..0.4.
    // For overall the camera orbits at y ≈ sin(50°)*5.5 ≈ 4.2; the default
    // clamp would force camera.y to 0.43 every frame → camera stuck at
    // ground level, top-down view destroyed.
    clampMinHeight: -Infinity,
    clampMaxHeight: Infinity,
    clampMaxHeightFar: Infinity,
    // Polar: 75°..85° (от вертикали). Камера остаётся "почти горизонтально",
    // ограничена сверху (нельзя подняться выше 15° над горизонтом — top-down
    // vid'a нет) и снизу (нельзя опуститься ниже 5° над горизонтом).
    orbitMinPolarAngle: (60 / 180) * Math.PI,
    orbitMaxPolarAngle: (85 / 180) * Math.PI,
    orbitMinDistance: 2,
    orbitMaxDistance: 6,
    orbitCenter: MAIN_ORBIT_CENTER,
  },
  colliders: [],
  splat: {
    defaultByQuality: {
      desktop: 'overall_2m',
      mobile: {
        high: 'overall_1m',
        middle: 'overall_1m',
        low: 'overall_600k',
      },
    },
    variants: [
      { key: 'overall_2m', url: OVERALL_2M_URL },
      { key: 'overall_1m', url: OVERALL_1M_URL },
      { key: 'overall_600k', url: OVERALL_600K_URL },
    ],
    revealAnimationDuration: 3.0,
    revealDistanceScale: 2.5,
    revealMode: '3d',
    revealOrigin: REVEAL_CENTER,
  },
  previewModel: {
    url: resolveCdnUrl('preview/Overall_preloader_v2_pnts_150000_meshopt(16).glb'),
    // Preview-tour anchor. Camera orbits around this point during the
    // preloader animation; after `previewTour.stop()` exit transition,
    // the active orbit center switches to `controls.orbitCenter`
    // (= MAIN_ORBIT_CENTER).
    orbitCenter: PREVIEW_ORBIT_CENTER,
    style: {
      // Match pre_loader_II baseline (~3px on-screen constant). Без
      // sizeAttenuation на zoom-in могут появиться зазоры между точками —
      // если станет критично, вернём attenuation: true.
      pointSizeFactor: 3.0,
      colorMultiplier: 1.0,     // оригинальные baked vertex colors (matches pre_loader_II); 1.5 ранее клипал highlights в шейдере
      // Точки появляются заметно медленнее после снижения colorMultiplier
      // (раньше яркость 5x маскировала animation). Ускоряем до уровня mina.
      appearanceDuration: 1.5,
      // Расширяем priority-зону за размер модели, чтобы distance penalty
      // не задерживала появление периферийных точек — сцена сразу выглядит
      // плотно набитой, а не растёт из центра.
      highPriorityAppearanceRadius: 1.5,
      wavesCenter: WAVES_CENTER,
      wavesAmplitude: 2.0,
      wavesPeriod: 1.0,
      wavesSpeed: 1.5,
      wavesColorIntensity: 5.0,
      wavesDisplacement: 0.02,
    },
  },
  // Nominal — required by the SceneConfig type; the teller assistant that read
  // it is gone.
  assistant: {
    position: { x: 0, y: 0, z: 0 },
    rotationY: 0,
    scale: 0.0000625,
    reflectionResolution: 128,
  },
  environment: {
    hemisphereLight: {
      skyColor: 0xffffff,
      groundColor: 0x444444,
      intensity: 0.6,
    },
    directionalLights: [
      {
        color: 0xffffff,
        intensity: 1.0,
        position: { x: 50, y: 100, z: 50 },
      },
    ],
    background: {
      color: 0x0f0f13,
      skybox: {
        url: resolveCdnUrl('environment/autumn_field_puresky_8k.jpg'),
        mobileUrl: resolveCdnUrl('environment/autumn_field_puresky_2k.jpg'),
        intensity: 1.0,
        blur: 0.0,
        rotation: [0, 205, 0],
      },
    },
  },
  // POI-чипы под инпутом скрыты: на overall есть только `navigationPois`
  // (кликабельные маркеры в 3D), которые не попадают в `$sceneSights` и не
  // имеют смысла в списке chip'ов. Сам чат остаётся включённым и интро
  // приходит по ключу `overall-scene` из бекенда (см. `SCENE_POI_ID`).
  disablePoiChips: true,
  // Подзаголовок "Journey to overall" в прелоадере не нужен — overall это
  // корневая карта, имя сцены не информативно для пользователя.
  disableSubtitle: true,
  // Кластеризация маркеров не нужна — 4 nav-POI разнесены далеко друг от
  // друга и не перекрываются.
  poi: {
    clusterEnabled: false,
  },
  // Длительность cinematic зависит от того, насколько камера далеко от
  // POI на момент клика: близкая точка — быстрый полёт, далёкая — медленный.
  // `thresholdDistance` в world-space units; подкрути в Leva-панели (через
  // `?dev`) или по ощущениям.
  navigationCinematicDuration: {
    close: 1.5,
    far: 2.5,
    thresholdDistance: 3.0,
  },
  // Top-level navigation POIs — клик ведёт в соответствующую под-сцену.
  // Координаты в world-space splat'а (unit-scale).
  //
  // **`position.y` ДОЛЖЕН лежать на видимой поверхности модели** (крыша
  // Каабы, верхушка горы Арафат, земля у лагеря Мина и т.п.), а не в
  // воздухе. Beacon-маркер парит над этой точкой на фиксированную
  // пиксельную дистанцию (константный подъём в `sdkPoiMarkers.css`)
  // — поэтому если поднять 3D-точку в воздух, маркер начнёт «гулять» по
  // вертикали относительно модели при изменении зума (perspective
  // compresses the y-offset at far zoom, expands at close zoom). См.
  // docs/architecture/poi.md → «Marker visual variant — beacon» → правило про anchor.
  //
  // `overviewCameraPosition` + `overviewCameraTarget` (опц.) — поза камеры,
  // к которой `OverallSceneOverlay` на мобильном свайпает при выборе слайда.
  // Если `overviewCameraTarget` не задан — fallback к `controls.orbitCenter`
  // (= `MAIN_ORBIT_CENTER`), это совпадает с центром орбиты в `?dev` → Orbit
  // Center при подборе. Подбирай позы в `?dev` → Camera и копируй сюда.
  navigationPois: [
    {
      targetSceneId: 'mina',
      labelKey: 'mina',
      position: { x: -0.1883, y: 0.10, z: 0.4232 },
      overviewCameraPosition: { x: 0.85, y: 1.26, z: 3.52 },
    },
    {
      targetSceneId: 'muzdalifah',
      labelKey: 'muzdalifah',
      position: { x: -1.3625, y: 0.10, z: -0.8246 },
      overviewCameraPosition: { x: -3.83, y: 0.89, z: -1.5 },
    },
    {
      targetSceneId: 'arafat',
      labelKey: 'arafat',
      position: { x: -2.5958, y: 0.28, z: -0.1402 },
      overviewCameraPosition: { x: -4.37, y: 0.45, z: -0.04 },
    },
    {
      targetSceneId: 'kaaba',
      labelKey: 'kaaba',
      position: { x: 2.484, y: 0.30, z: -0.3401 },
      overviewCameraPosition: { x: 5.19, y: 1.08, z: -0.76 },
    },
  ],
};
